
import React, { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import ScrollToTopButton from './ScrollToTopButton';
import Footer from './Footer';
import Navbar from './Navbar';

interface PageContainerProps {
  children: React.ReactNode;
  withVerticalPadding?: boolean;
}

/**
 * This component helps with accessibility by announcing page changes to screen readers
 * and provides a consistent wrapper for all pages with improved SEO tracking
 * 
 * IMPORTANT: This component renders both the Navbar and Footer components.
 * Individual page components should NOT include these components directly.
 */
const PageContainer = ({ children, withVerticalPadding = true }: PageContainerProps) => {
  const location = useLocation();
  
  // Update page title and announce page changes for screen readers
  useEffect(() => {
    // Focus management for screen readers - create or get the main content element
    let mainContent = document.getElementById('main-content');
    
    // If main-content doesn't exist, find the first main element
    if (!mainContent) {
      mainContent = document.querySelector('main');
    }
    
    // If we found an element to focus, set focus and proper attributes
    if (mainContent) {
      // Add tabindex=-1 to allow focus but not in tab order
      if (!mainContent.hasAttribute('tabindex')) {
        mainContent.setAttribute('tabindex', '-1');
      }
      
      // Add proper role if missing
      if (!mainContent.hasAttribute('role')) {
        mainContent.setAttribute('role', 'main');
      }
      
      // Focus the element for screen readers
      mainContent.focus({ preventScroll: true });
    }
    
    // Scroll to top on page change
    window.scrollTo(0, 0);
    
    // Track analytics with dynamic imports for code splitting
    import('@/utils/analyticsService').then(({ trackPageView, trackSEOMetrics, trackLeadinfoPageView }) => {
      // Track page view in Google Analytics (only on non-SaaS routes)
      trackPageView(location.pathname);
      
      // Track SEO metrics after page load (only on non-SaaS routes)
      const isSaaSRoute = location.pathname.startsWith('/saas');
      if (!isSaaSRoute) {
        setTimeout(() => {
          trackSEOMetrics();
        }, 1000);
      }
      
      // Track with Leadinfo (only on non-SaaS routes)
      trackLeadinfoPageView();
    });
    
    // Analytics tracking
    console.log(`Page viewed: ${location.pathname}`);
  }, [location]);

  return (
    <div className="page-container">
      <Navbar />
      {children}
      <Footer />
      <ScrollToTopButton />
    </div>
  );
};

export default PageContainer;
